/**
 * Types d'occasions pour les réservations de restaurant
 *
 * ⚠️ SYNCHRONISATION OBLIGATOIRE avec le backend
 * Fichier backend : backend/src/modules/restaurants/constants/occasionTypes.js
 *
 * Ces clés invariantes sont stockées en base de données et ne doivent JAMAIS être modifiées.
 * L'affichage traduit se fait via les fichiers de traduction (locales/langs/*.json).
 *
 * @module restaurants/OccasionTypes
 * @author Équipe Brydge
 * @since 2.0.0
 */

/**
 * Clés invariantes des types d'occasions
 *
 * Ces valeurs sont stockées en base de données.
 * Elles correspondent aux clés de traduction dans les fichiers de langue.
 *
 * @constant {Object}
 */
export const OCCASION_TYPES = {
  NONE: '',
  BIRTHDAY: 'BIRTHDAY',
  BUSINESS: 'BUSINESS',
  WEDDING: 'WEDDING',
  ENGAGEMENT: 'ENGAGEMENT',
  ANNIVERSARY: 'ANNIVERSARY',
  FAMILY: 'FAMILY',
  OTHER: 'OTHER',
};

/**
 * Liste ordonnée des types d'occasions
 *
 * Utilisée pour générer les options de sélection dans les interfaces.
 * L'ordre correspond à l'ordre d'affichage dans les formulaires.
 *
 * @constant {Array<string>}
 */
export const OCCASION_VALUES = [
  OCCASION_TYPES.NONE,
  OCCASION_TYPES.BIRTHDAY,
  OCCASION_TYPES.BUSINESS,
  OCCASION_TYPES.WEDDING,
  OCCASION_TYPES.ENGAGEMENT,
  OCCASION_TYPES.ANNIVERSARY,
  OCCASION_TYPES.FAMILY,
  OCCASION_TYPES.OTHER,
];

/**
 * Labels en français pour le Dashboard (fallback si pas de traduction)
 *
 * Utilisé uniquement pour le Dashboard qui est 100% français.
 *
 * @constant {Object}
 */
export const OCCASION_LABELS_FR = {
  [OCCASION_TYPES.NONE]: 'Aucune',
  [OCCASION_TYPES.BIRTHDAY]: 'Anniversaire',
  [OCCASION_TYPES.BUSINESS]: 'Repas d\'affaires',
  [OCCASION_TYPES.WEDDING]: 'Mariage',
  [OCCASION_TYPES.ENGAGEMENT]: 'Fiançailles',
  [OCCASION_TYPES.ANNIVERSARY]: 'Anniversaire de mariage',
  [OCCASION_TYPES.FAMILY]: 'Fête de famille',
  [OCCASION_TYPES.OTHER]: 'Autre',
};

/**
 * Obtient le label en français pour le Dashboard
 *
 * @param {string} key - Clé de l'occasion
 * @return {string} Label en français
 *
 * @example
 * getOccasionLabelFR('BIRTHDAY') // "Anniversaire"
 * getOccasionLabelFR('') // "Aucune"
 */
export const getOccasionLabelFR = (key) => {
  return OCCASION_LABELS_FR[key] || OCCASION_LABELS_FR[OCCASION_TYPES.NONE];
};

/**
 * Obtient le label traduit pour une clé d'occasion (Widgets multilingues)
 *
 * Utilisé pour les widgets avec i18next.
 *
 * @param {string} key - Clé de l'occasion
 * @param {Function} t - Fonction de traduction i18next
 * @return {string} Label traduit
 *
 * @example
 * getOccasionLabel('BIRTHDAY', t) // "Anniversaire" (FR)
 * getOccasionLabel('BIRTHDAY', t) // "Birthday" (EN)
 * getOccasionLabel('BIRTHDAY', t) // "Geburtstag" (DE)
 */
export const getOccasionLabel = (key, t) => {
  if (!key) {
    return t ? t('widgets.reservation.form.noOccasion') : 'Aucune occasion particulière';
  }

  const translationKey = `widgets.reservation.form.occasions.${key.toLowerCase()}`;
  return t ? t(translationKey) : key;
};
